import { z } from "zod";
import { createEndpoint, createEndpointWithPagination, StandardSuccessResponseSchema } from "../createEndpoint";
import type { HttpClient, QueryParams } from "../httpClient";
import {
    AssignExpertLevelRequestSchema,
    CreateExpertLevelRequestSchema,
    ExpertLevelAssignmentSchema,
    ExpertLevelSchema,
    UpdateExpertLevelRequestSchema,
} from "../schemas/expert-levels";

export interface AdminExpertLevelsListQueryParams extends QueryParams {
    is_active?: boolean;
    per_page?: number;
    cursor?: string;
}

export interface AdminExpertLevelHistoryQueryParams extends QueryParams {
    per_page?: number;
    cursor?: string;
}

export function createAdminExpertLevelsApi(http: HttpClient, baseUrl: string) {
    const adminExpertLevelsBaseUrl = `${baseUrl}/admin/expert-levels`;
    const adminExpertsBaseUrl = `${baseUrl}/admin/experts`;

    return {
        listLevels: createEndpointWithPagination<
            z.infer<typeof ExpertLevelSchema>,
            unknown,
            unknown,
            AdminExpertLevelsListQueryParams
        >(http, "GET", "", {
            base: adminExpertLevelsBaseUrl,
            itemSchema: ExpertLevelSchema,
        }),

        getLevel: createEndpoint<
            z.infer<typeof ExpertLevelSchema>,
            unknown,
            QueryParams,
            { expertLevel: number }
        >(http, "GET", "/:expertLevel", {
            base: adminExpertLevelsBaseUrl,
            responseSchema: ExpertLevelSchema,
            params: ["expertLevel"],
        }),

        createLevel: createEndpoint<
            z.infer<typeof ExpertLevelSchema>,
            z.input<typeof CreateExpertLevelRequestSchema>
        >(http, "POST", "", {
            base: adminExpertLevelsBaseUrl,
            responseSchema: ExpertLevelSchema,
        }),

        updateLevel: createEndpoint<
            z.infer<typeof ExpertLevelSchema>,
            z.input<typeof UpdateExpertLevelRequestSchema>,
            QueryParams,
            { expertLevel: number }
        >(http, "PUT", "/:expertLevel", {
            base: adminExpertLevelsBaseUrl,
            responseSchema: ExpertLevelSchema,
            params: ["expertLevel"],
        }),

        deleteLevel: createEndpoint<
            z.infer<typeof StandardSuccessResponseSchema>,
            unknown,
            QueryParams,
            { expertLevel: number }
        >(http, "DELETE", "/:expertLevel", {
            base: adminExpertLevelsBaseUrl,
            responseSchema: StandardSuccessResponseSchema,
            params: ["expertLevel"],
        }),

        assignToExpert: createEndpoint<
            z.infer<typeof ExpertLevelAssignmentSchema>,
            z.input<typeof AssignExpertLevelRequestSchema>,
            QueryParams,
            { expert: number }
        >(http, "POST", "/:expert/expert-level", {
            base: adminExpertsBaseUrl,
            responseSchema: ExpertLevelAssignmentSchema,
            params: ["expert"],
        }),

        getExpertLevelHistory: createEndpointWithPagination<
            z.infer<typeof ExpertLevelAssignmentSchema>,
            unknown,
            unknown,
            AdminExpertLevelHistoryQueryParams,
            { expert: number }
        >(http, "GET", "/:expert/expert-level/history", {
            base: adminExpertsBaseUrl,
            itemSchema: ExpertLevelAssignmentSchema,
            params: ["expert"],
        }),
    };
}

export type AdminExpertLevelsApi = ReturnType<typeof createAdminExpertLevelsApi>;
